<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";
import { nextTick, ref, watch } from "vue";
import OmniOverlay from "@/components/omni/OmniOverlay.vue";
import type { GoogleSheetsInfo } from "@/api/trips/dto/trip.dto";
import LoadingIcon from "@/components/icons/LoadingIcon.vue";
import { useError } from "@/composables/useError";
import { useSnackbarStore } from "@/store/snackbar.store";
import { useDownload } from "@/composables/reactive/useDownload";

const api = new API();
let rateConfirmationMarkup = "";

const { downloadFile, downloading } = useDownload();

const model = defineModel<boolean>({ default: false });
const props = defineProps<{
  routeId: number;
}>();

const { t } = useI18n();
const { showAPIError } = useError();
const { showSnackbarSuccess } = useSnackbarStore();

const companyDetails = ref<GoogleSheetsInfo | null>(null);
const loading = ref<boolean>(false);
const signing = ref<boolean>(false);
const iframeRef = ref<HTMLIFrameElement | null>(null);

async function loadRateConfirmation() {
  loading.value = true;

  try {
    const response = await api.events.loadRateConfirmation(props.routeId);
    if (!response.data || !response.success) {
      return;
    }

    rateConfirmationMarkup = response.data.content;
    companyDetails.value = response.data.googleSheetsInfo;
  } finally {
    loading.value = false;
  }
}

async function onIframeLoad() {
  await nextTick();
  const iframe = iframeRef.value;
  if (!iframe) {
    return;
  }

  const doc = iframe.contentDocument!;

  doc.open();
  doc.write(rateConfirmationMarkup);
  doc.close();

  doc.body.contentEditable = "true";
}

function syncRateConfirmationMarkup() {
  const doc = iframeRef.value?.contentDocument;
  if (!doc) {
    return;
  }

  const doctype = doc.doctype ? `<!DOCTYPE ${doc.doctype.name}>` : "";

  rateConfirmationMarkup = doctype + doc.documentElement.outerHTML;
}

async function download() {
  syncRateConfirmationMarkup();
  await downloadFile(async () => {
    try {
      const response = await api.events.downloadRateConfirmation(props.routeId, {
        content: rateConfirmationMarkup,
      });

      if (!response.data || !response.success) {
        showAPIError(response, t("trip.error-downloading-rate-confirmation"));
        return;
      }

      return response.data;
    } catch (error) {
      console.error(error);
    }
  }, "rate-confirmation");
}

async function sign() {
  syncRateConfirmationMarkup();
  signing.value = true;
  try {
    const response = await api.events.signRateConfirmation(props.routeId, {
      content: rateConfirmationMarkup,
    });

    if (!response.data || !response.success || !response.data.success) {
      showAPIError(response, t("trip.error-sign-rate-confirmation"));

      return;
    }

    showSnackbarSuccess(t("trip.success-sign-rate-confirmation"));

    model.value = false;
  } catch (error) {
    console.error(error);
  } finally {
    signing.value = false;
  }
}

watch(model, async (value) => {
  if (!value) {
    rateConfirmationMarkup = "";
    companyDetails.value = null;
  } else {
    await loadRateConfirmation();
  }
});
</script>

<template>
  <omni-overlay v-model="model" class="rate-confirmation">
    <template #header>
      <div class="d-flex align-center justify-end w-100 mr-8 col-gap-8">
        <v-btn
          :disabled="signing"
          :loading="downloading"
          class="text-uppercase"
          color="white"
          prepend-icon="mdi-tray-arrow-down"
          size="large"
          variant="outlined"
          @click="download"
        >
          {{ t("general.download") }}
        </v-btn>

        <v-btn
          :disabled="downloading"
          :loading="signing"
          class="text-uppercase"
          color="primary"
          prepend-icon="mdi-send-outline"
          size="large"
          @click="sign"
        >
          {{ t("trip.send-rate-confirmation") }}
        </v-btn>
      </div>
    </template>

    <div class="rate-confirmation__content">
      <div class="rate-confirmation__pdf">
        <template v-if="loading">
          <loading-icon size="85" />
        </template>

        <template v-else>
          <iframe ref="iframeRef" class="w-100 h-100" @load="onIframeLoad"></iframe>
        </template>
      </div>

      <div v-if="companyDetails" class="rate-confirmation__details">
        <div class="text-grey-darken-3 font-weight-500 font-size-16">{{ t("trip.owner-google-sheets-details") }}</div>

        <div class="text-grey-darken-3 font-size-16 font-weight-500 mt-3">
          {{ companyDetails.ownerCompanyName }}
        </div>

        <div class="text-grey-darken-2 font-size-16">{{ companyDetails.ownerCompanyAddress }}</div>
      </div>
    </div>
  </omni-overlay>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.rate-confirmation {
  &__content {
    height: 100%;
    position: relative;
  }

  &__pdf {
    min-width: 800px;
    background-color: $white;
    max-width: fit-content;
    margin: 0 auto;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    border-radius: 4px;
  }

  &__details {
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 10px 20px 0 rgba(18, 38, 63, 0.04);
    background: $grey-light3;
    height: fit-content;
    position: absolute;
    top: 0;
    right: 0;
    max-width: 350px;
    box-sizing: content-box;
  }
}
</style>
